import type { Article } from '../../../../api/_lib/lovinMalta';
import { formatRelativeTime } from '../../../utils/widgetFormatters';
import './LatestNews.css';

interface Props {
  article: Article;
  /** `compact` (default) is the rail tile — 80×80 thumb + body
   *  next to it. `card` is the bigger image-led variant used by
   *  the polls_open below-hero band — image on top filling the
   *  card width with a generous aspect ratio, text below. */
  variant?: 'compact' | 'card';
}

export default function ArticleTile({ article, variant = 'compact' }: Props) {
  const className =
    variant === 'card' ? 'article-tile article-tile--card' : 'article-tile';
  return (
    <a
      href={article.link}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <div className="article-tile__thumb" aria-hidden="true">
        {article.thumbnail ? (
          // Lovin Malta's CDN serves a 403 when the Referer is set to
          // any host other than lovinmalta.com (anti-hotlink rule).
          // `referrerPolicy="no-referrer"` strips the header entirely,
          // which bypasses the check regardless of where this page is
          // served from (localhost dev, Vercel preview, or the live
          // iframe embed on lovinmalta.com itself).
          <img
            src={article.thumbnail}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="article-tile__thumb-placeholder">
            <svg viewBox="0 0 24 24" fill="none" width="24" height="24">
              <path
                d="M4 5h16v14H4zM4 9h16M8 5v14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
      </div>
      <div className="article-tile__body">
        <span className="article-tile__category">{article.category}</span>
        <h3 className="article-tile__title">{article.title}</h3>
        <p className="article-tile__meta">
          <span className="article-tile__author">{article.author}</span>
          <span aria-hidden="true"> · </span>
          <time dateTime={article.date}>{formatRelativeTime(article.date)}</time>
        </p>
      </div>
    </a>
  );
}
